import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SearchProvider } from "./contexts/SearchContext";
import { CartProvider } from "./contexts/CartContext";
import { UserProvider } from "./contexts/UserContext";
import { OrganizationProvider, useOrg } from "./contexts/OrganizationContext";
import { MapProvider } from "./contexts/MapContext";
import Announcements from "./components/Announcements/Announcements";
import PersistentMapContainer from "./components/Map/PersistentMapContainer";
import HomePage from "./pages/HomePage";
import CartPage from "./pages/CartPage";
import ResourceListDetailPage from "./pages/ResourceListDetailPage";
import SurveyPage from "./pages/SurveyPage";
import ResponseSetPage from "./pages/ResponseSetPage";
import SearchPage from "./pages/SearchPage";
import SearchResultDetailPage from "./pages/SearchResultDetailPage";
import AfterSignInPage from "./pages/AfterSignInPage";
import ResourceListResourcePage from "./pages/ResourceListResourcePage";

function AnnouncementsPortal() {
  const { organization } = useOrg();
  const [announcements, setAnnouncements] = React.useState([]);
  const container = document.getElementById("announcement-region");

  React.useEffect(() => {
    const feedUrl = organization.announcementsFeedUrl;
    if (!feedUrl) return;
    fetch(feedUrl)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setAnnouncements(data);
        }
      })
      .catch(() => {});
  }, [organization.announcementsFeedUrl]);

  if (!container || announcements.length === 0) return null;

  return ReactDOM.createPortal(
    <Announcements announcements={announcements} />,
    container
  );
}

function RouteTracker() {
  const location = useLocation();
  const { organization } = useOrg();
  React.useEffect(() => {
    if (organization.flags?.enableGoogleAnalytics && typeof ga === "function") {
      ga("set", "page", location.pathname + location.search);
      ga("send", "pageview");
    }
  }, [location, organization.flags?.enableGoogleAnalytics]);
  return null;
}

function App() {
  return (
    <OrganizationProvider>
      <UserProvider>
        <SearchProvider>
          <CartProvider>
            <MapProvider>
              <AnnouncementsPortal />
              <PersistentMapContainer />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <RouteTracker />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/my211/resource-lists/:id" element={<ResourceListDetailPage />} />
                  <Route path="/my211/resource-lists/:id/resource/:resourceId" element={<ResourceListResourcePage />} />
                  <Route path="/shared/resource-lists/:id" element={<ResourceListDetailPage shared />} />
                  <Route path="/shared/resource-lists/:id/resource/:resourceId" element={<ResourceListResourcePage shared />} />
                  <Route path="/assessments/:id" element={<SurveyPage />} />
                  <Route path="/assessments/response_sets/confirmation/:token" element={<ResponseSetPage />} />
                  <Route path="/after_sign_in" element={<AfterSignInPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/search/:id" element={<SearchResultDetailPage />} />
                  <Route path="/search/:id/cart" element={<SearchResultDetailPage fromCart />} />
                  <Route path="/search/:id/print" element={<SearchResultDetailPage autoPrint />} />
                  <Route path="*" element={null} />
                </Routes>
              </BrowserRouter>
            </MapProvider>
          </CartProvider>
        </SearchProvider>
      </UserProvider>
    </OrganizationProvider>
  );
}

export default App;
